import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLocale } from '../utils/locale';
import { formatPrice } from '../utils/format';
import { getEventCapacity } from '../utils/event';
import { resolveUploadUrl } from '../utils/url';

export default function EventCard({ event }) {
  const { t, i18n } = useTranslation();
  // Handle both GA and seated events for price display
  const getLowestPrice = () => {
    if (event.tickets && event.tickets.length > 0) {
      return Math.min(...event.tickets.map(ticket => ticket.price));
    }
    if (event.venueLayout?.sections && event.venueLayout.sections.length > 0) {
      return Math.min(...event.venueLayout.sections.map(s => s.price));
    }
    return 0;
  };

  const lowestPrice = getLowestPrice();
  const eventDate = new Date(event.date);
  const isSeated = event.venueLayout?.type === 'seated';

  // Calculate availability
  const { total, available } = getEventCapacity(event);
  const availability = total > 0 ? { total, available } : null;
  const isAlmostSoldOut = availability && (availability.available / availability.total) < 0.1;

  return (
    <Link
      to={`/event/${event.id}`}
      className="group block card-hover overflow-hidden"
    >
      {/* Event Image/Banner */}
      <div className="relative h-44 bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center overflow-hidden">
        <span className="text-white/20 text-8xl font-bold select-none group-hover:scale-110 transition-transform duration-300">
          {event.title.charAt(0)}
        </span>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {isSeated && (
            <span className="bg-white/90 backdrop-blur-sm text-brand-700 text-xs font-medium px-2 py-1 rounded-md">
              {t('eventCard.reservedSeating')}
            </span>
          )}
          {isAlmostSoldOut && (
            <span className="bg-amber-500 text-white text-xs font-medium px-2 py-1 rounded-md animate-pulse-slow">
              {t('eventCard.almostSoldOut')}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Date Badge */}
        <div className="flex items-start gap-3 mb-3">
          <div className="flex-shrink-0 text-center">
            <div className="text-xs font-semibold text-brand-600 uppercase tracking-wide">
              {eventDate.toLocaleDateString(getLocale(i18n.language), { month: 'short' })}
            </div>
            <div className="text-xl font-bold text-gray-900">
              {eventDate.getDate()}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 group-hover:text-brand-600 transition-colors line-clamp-2">
              {event.title}
            </h3>
            <p className="text-gray-500 text-sm mt-0.5">
              {eventDate.toLocaleTimeString(getLocale(i18n.language), {
                hour: 'numeric',
                minute: '2-digit',
              })}
            </p>
          </div>
        </div>

        {/* Venue */}
        {event.venue && (
          <div className="flex items-center gap-1.5 text-gray-500 text-sm mb-3">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="truncate">{event.venue}</span>
          </div>
        )}

        {/* Price and Organizer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div>
            <span className="text-sm text-gray-500">{t('common.from')} </span>
            <span className="text-lg font-semibold text-gray-900">
              {formatPrice(lowestPrice)}
            </span>
          </div>
          {event.organizer && (
            <div className="flex items-center gap-1.5 truncate max-w-[140px]">
              {resolveUploadUrl(event.organizer.logoUrl) ? (
                <img
                  src={resolveUploadUrl(event.organizer.logoUrl)}
                  alt=""
                  className="w-4 h-4 rounded-full object-cover flex-shrink-0"
                />
              ) : null}
              <span className="text-xs text-gray-400 truncate">
                {t('common.by')} {event.organizer.businessName}
              </span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
